import React, { useEffect, useState } from 'react';
import { Truck, Clock } from 'lucide-react';

/**
 * Slim banner under the header showing a live countdown to the daily
 * shipping cutoff (5:00 PM Pacific). After cutoff, switches to
 * "ships tomorrow" (or next business day on weekends).
 */
const CUTOFF_HOUR_PT = 17; // 5:00 PM Pacific

const getPacificParts = (date = new Date()) => {
  // Use Intl to get hour/min/sec in America/Los_Angeles regardless of host TZ
  const fmt = new Intl.DateTimeFormat('en-US', {
    timeZone: 'America/Los_Angeles',
    hour12: false,
    weekday: 'short',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
  const parts = fmt.formatToParts(date).reduce((acc, p) => {
    if (p.type !== 'literal') acc[p.type] = p.value;
    return acc;
  }, {});
  return {
    weekday: parts.weekday, // "Mon", "Tue", ...
    hour: parseInt(parts.hour, 10) % 24,
    minute: parseInt(parts.minute, 10),
    second: parseInt(parts.second, 10),
  };
};

const nextBusinessDayLabel = (weekday) => {
  // Given today's weekday, what's the next ship day label?
  const order = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  const idx = order.indexOf(weekday);
  let nextIdx = (idx + 1) % 7;
  if (order[nextIdx] === 'Sat') nextIdx = (nextIdx + 2) % 7; // skip to Mon
  else if (order[nextIdx] === 'Sun') nextIdx = (nextIdx + 1) % 7; // skip to Mon
  const full = {
    Sun: 'Sunday', Mon: 'Monday', Tue: 'Tuesday', Wed: 'Wednesday',
    Thu: 'Thursday', Fri: 'Friday', Sat: 'Saturday',
  };
  return full[order[nextIdx]];
};

const pad = (n) => String(n).padStart(2, '0');

const ShipsTodayBanner = () => {
  const [tick, setTick] = useState(0);

  useEffect(() => {
    const id = setInterval(() => setTick((t) => t + 1), 1000);
    return () => clearInterval(id);
  }, []);

  const { weekday, hour, minute, second } = getPacificParts(new Date(Date.now() + tick * 0));
  const isWeekend = weekday === 'Sat' || weekday === 'Sun';

  let mode; // 'today' | 'tomorrow'
  let h = 0, m = 0, s = 0;
  if (!isWeekend && hour < CUTOFF_HOUR_PT) {
    mode = 'today';
    const totalSecLeft =
      (CUTOFF_HOUR_PT - hour) * 3600 - minute * 60 - second;
    h = Math.floor(totalSecLeft / 3600);
    m = Math.floor((totalSecLeft % 3600) / 60);
    s = totalSecLeft % 60;
  } else {
    mode = 'tomorrow';
  }

  const nextDay = nextBusinessDayLabel(weekday);

  return (
    <div
      data-testid="ships-today-banner"
      className="w-full"
      style={{
        background: 'linear-gradient(90deg, rgba(212, 163, 115, 0.12) 0%, rgba(212, 163, 115, 0.06) 50%, rgba(212, 163, 115, 0.12) 100%)',
        borderBottom: '1px solid rgba(212, 163, 115, 0.25)',
      }}
    >
      <div className="container mx-auto px-4 py-2 flex items-center justify-center gap-3 text-xs sm:text-sm">
        <Truck className="h-4 w-4 flex-shrink-0" style={{ color: '#D4A373' }} />
        {mode === 'today' ? (
          <>
            <span style={{ color: '#F7F3EE' }}>
              Order in{' '}
              <span
                data-testid="ships-today-countdown"
                className="font-bold tabular-nums"
                style={{ color: '#D4A373' }}
              >
                {h > 0 && `${h}h `}{pad(m)}m {pad(s)}s
              </span>
              {' '}— ships <span className="font-bold uppercase tracking-wider" style={{ color: '#86EFAC' }}>today</span>
            </span>
            <Clock className="h-3.5 w-3.5 hidden sm:block" style={{ color: '#B5A79E' }} />
          </>
        ) : (
          <span style={{ color: '#F7F3EE' }}>
            Order today — ships{' '}
            <span className="font-bold" style={{ color: '#D4A373' }}>
              {nextDay}
            </span>
          </span>
        )}
      </div>
    </div>
  );
};

export default ShipsTodayBanner;
